import { __, sprintf } from "@wordpress/i18n";
import ReactTooltip from "react-tooltip";
import { useEffect, useRef, useState } from "react";
import { ajaxCall } from "../Helpers/AjaxHelper";
import ActionButton from "./ActionButton";
import { ReactComponent as View } from "../assets/view.svg";
import { ReactComponent as Publish } from "../assets/publish.svg";
import { ReactComponent as Trash } from "../assets/trash.svg";
import { ReactComponent as Loader } from "../assets/loader.svg";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";

const TableHeading = ({ selected, setSelectAll }) => {
  const inputRef = useRef();
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.checked = selected;
    }
  }, [selected]);
  return (
    <div className="border-b-gray-600 grid grid-cols-12 text-sb-gray">
      <div className="col-span-3 flex flex-row items-baseline">
        <div className="mt-0.5 pr-3">
          <input
            ref={inputRef}
            type="checkbox"
            className="accent-gray-border accent"
            onChange={(evt) => {
              setSelectAll(evt.target.checked);
            }}
          />
        </div>
        <div>{__("Channel Title", "feeds-for-youtube")}</div>
      </div>
      <div className="col-span-3">{__("Published", "feeds-for-youtube")}</div>
      <div className="col-span-3">{__("Drafts", "feeds-for-youtube")}</div>
      <div className="col-span-3">{__("Actions", "feeds-for-youtube")}</div>
    </div>
  );
};

const Channel = ({
  channel_title,
  published,
  drafts,
  selected,
  id,
  reload,
  register,
}) => {
  const { ref, ...rest } = register("channel_" + id);
  const inputRef = useRef();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.checked = selected;
    }
  }, [selected]);

  const channelsListingPage = new URL(window.sby_settings.single_videos_list);
  channelsListingPage.searchParams.append("sby_channel_filter", channel_title);

  return (
    <div className="sby-channel-item col-span-full px-4">
      <div className="border-b-gray-600 grid grid-cols-12 text-sb-gray py-3 items-baseline">
        <div className="col-span-3 flex flex-row items-baseline">
          <div className="mt-0.5 pr-3">
            <input
              ref={(evt) => {
                ref(evt);
                inputRef.current = evt;
              }}
              type="checkbox"
              className="accent-gray-border accent"
              value={channel_title}
              {...rest}
            />
          </div>
          <a
            className="sby-channel-title"
            href={channelsListingPage.toString()}
          >
            {channel_title}
          </a>
        </div>
        <div className="col-span-3">{published ?? 0}</div>
        <div className="col-span-3">{drafts ?? 0}</div>
        <div className="col-span-3 flex flex-row">
          <ReactTooltip
            className="sby-tooltip"
            place="top"
            effect="solid"
            arrowColor="#2C324C"
            id={`channel-${id}`}
          />
          <ActionButton
            tooltip={__("View All", "feeds-for-youtube")}
            icon={<View />}
            id={`channel-${id}`}
            link={`${channelsListingPage.toString()}`}
          />
          <ActionButton
            tooltip={__("Publish All", "feeds-for-youtube")}
            icon={<Publish />}
            id={`channel-${id}`}
            onClick={() => {
              return ajaxCall("sby_all_videos_action", {
                channel: channel_title,
                perform: "publish",
              }).then(() => {
                reload();
              });
            }}
          />
          <ActionButton
            tooltip={__("Delete All", "feeds-for-youtube")}
            className="sby-action-btn-red"
            icon={<Trash />}
            onClick={() => {
              return ajaxCall("sby_all_videos_action", {
                channel: channel_title,
                perform: "delete",
              }).then(() => {
                reload();
              });
            }}
            id={`channel-${id}`}
          />
        </div>
      </div>
    </div>
  );
};

const LoadingIndicator = () => {
  return (
    <div className="loading flex flex-row justify-center py-10">
      <Loader />
    </div>
  );
};

const BulkActions = ({ isSubmitting, register }) => {
  return (
    <div className="flex flex-row">
      <div className="pr-2">
        <select
          className="sby-select"
          {...register("bulk_action")}
          defaultValue=""
        >
          <option value="" disabled>
            {__("Bulk Actions", "feeds-for-youtube")}
          </option>
          <option value="publish">
            {__("Publish All", "feeds-for-youtube")}
          </option>
          <option value="delete">
            {__("Delete All", "feeds-for-youtube")}
          </option>
        </select>
      </div>
      <button
        type="submit"
        className={`sby-btn ${isSubmitting ? "loading" : ""}`}
      >
        {isSubmitting && <Loader />}
        {__("Apply", "feeds-for-youtube")}
      </button>
    </div>
  );
};

const PaginationControls = ({
  setCurrentPage,
  currentPage,
  count,
  limit,
  total,
}) => {
  const [, setSearchParams] = useSearchParams();
  const maxPageCount = Math.ceil(total / limit);
  const PaginationButton = ({ action, disabled, text }) => {
    return (
      <a
        onClick={() => {
          if (!disabled) {
            action();
          }
        }}
        className={`sby-pagination-button ${disabled ? "disabled" : ""}`}
      >
        <span>{text}</span>
      </a>
    );
  };

  useEffect(() => {
    setSearchParams({
      page: currentPage,
    });
  }, [currentPage]);
  return (
    <div className="flex flex-row items-baseline">
      <span className="font-bold">{sprintf("%s Items", count)}</span>
      <PaginationButton
        text="«"
        action={() => {
          setCurrentPage(0);
        }}
        disabled={currentPage === 0}
      />
      <PaginationButton
        text="‹"
        action={() => {
          setCurrentPage(currentPage - 1);
        }}
        disabled={currentPage === 0}
      />
      {sprintf(
        __("%s of %s", "feeds-for-youtube"),
        currentPage + 1,
        maxPageCount
      )}
      <PaginationButton
        text="›"
        action={() => {
          setCurrentPage(currentPage + 1);
        }}
        disabled={currentPage === maxPageCount - 1}
      />
      <PaginationButton
        text="»"
        action={() => {
          setCurrentPage(maxPageCount - 1);
        }}
        disabled={currentPage === maxPageCount - 1}
      />
    </div>
  );
};
const VideosTable = () => {
  const [searchParams] = useSearchParams();
  const { register, handleSubmit } = useForm();
  const [videos, setVideos] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page") ?? 0)
  );
  const [total, setTotal] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [reload, setReload] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const DoReload = () => {
    setReload(reload + 1);
  };
  useEffect(() => {
    setVideos([]);
    setIsLoading(true);
    ajaxCall("sby_get_single_videos", {
      page: currentPage,
    })
      .then((res) => res.json())
      .then(({ data }) => {
        setTotal(data.total_result);
        setVideos(data.results);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentPage, reload]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <form
      onSubmit={handleSubmit((data) => {
        setIsSubmitting(true);
        const channels = [];
        Object.keys(data).forEach((key) => {
          const inputObject = document.querySelector(`input[name=${key}]`);
          if (inputObject && inputObject.checked) {
            channels.push(inputObject.value);
          }
        });
        ajaxCall("sby_all_videos_action", {
          perform: data.bulk_action,
          channel: channels,
        }).finally(() => {
          setIsSubmitting(false);
          DoReload();
        });
      })}
    >
      <div className="sby-data-table pt-10 text-sb-gray">
        <div className="flex flex-row justify-between">
          <BulkActions isSubmitting={isSubmitting} register={register} />
          <PaginationControls
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            count={videos.length}
            total={total}
            limit={5}
          />
        </div>
        <div className="w-full bg-white mt-2 shadow shadow-black/5 grid grid-cols-12  py-2 text-left">
          <div className="col-span-full sby-single-video-table-head border-b border-gray-border px-4 pb-2">
            <TableHeading selected={selectAll} setSelectAll={setSelectAll} />
          </div>
          <div className="col-span-full">
            {isLoading && <LoadingIndicator />}
            {!isLoading && videos.length === 0 && (
              <div className="flex flex-row justify-center py-10">
                <span>{__("No single videos found", "feeds-for-youtube")}</span>
              </div>
            )}
            {videos.length > 0 &&
              videos.map((video, index) => (
                <Channel
                  id={index}
                  channel_title={video.title}
                  published={video.publish}
                  drafts={video.draft}
                  selected={selectAll}
                  key={index}
                  reload={DoReload}
                  register={register}
                />
              ))}
          </div>
          <div className="col-span-full sby-single-video-table-foot border-t border-gray-border px-4 pt-2">
            <TableHeading selected={selectAll} setSelectAll={setSelectAll} />
          </div>
        </div>
      </div>
    </form>
  );
};

export default VideosTable;
